<template>
  <div class="p-4">
    <div class="dashboard">PrintNode</div>

    <!-- Settings Section -->
    <div class="mb-6 border rounded-lg p-4">
      <h2 class="text-lg font-semibold mb-3">Settings</h2>

      <div class="flex flex-col gap-3 max-w-lg">
        <!-- API Key -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1"
            >API Key</label
          >
          <div class="flex gap-2">
            <input
              v-model="apiKeyInput"
              type="password"
              class="border rounded px-3 py-1.5 flex-1 text-sm"
              :placeholder="
                settings.apiKeySet
                  ? settings.apiKeyMasked
                  : 'Enter PrintNode API key'
              "
            />
            <button
              class="dark-button text-sm"
              @click="saveApiKey"
              :disabled="!apiKeyInput || savingKey"
            >
              {{ savingKey ? "Saving..." : "Save" }}
            </button>
          </div>
        </div>

        <!-- Enabled toggle -->
        <div class="flex items-center gap-2">
          <label class="text-sm font-medium text-gray-700">Enabled</label>
          <button
            class="px-3 py-1 rounded text-sm"
            :class="
              settings.enabled === '1'
                ? 'bg-green-500 text-white'
                : 'bg-gray-300 text-gray-600'
            "
            @click="toggleEnabled"
          >
            {{ settings.enabled === "1" ? "ON" : "OFF" }}
          </button>
        </div>

        <!-- Test connection -->
        <div>
          <button
            class="dark-button text-sm"
            @click="testConnection"
            :disabled="testingConnection"
          >
            {{ testingConnection ? "Testing..." : "Test Connection" }}
          </button>
          <span
            v-if="connectionResult"
            class="ml-2 text-sm"
            :class="connectionOk ? 'text-green-600' : 'text-red-600'"
          >
            {{ connectionResult }}
          </span>
        </div>
      </div>
    </div>

    <!-- Sync & Printers Section -->
    <div class="mb-6 border rounded-lg p-4">
      <div class="flex items-center justify-between mb-3">
        <h2 class="text-lg font-semibold">Printers</h2>
        <button
          class="dark-button text-sm"
          @click="syncPrinters"
          :disabled="syncing"
        >
          {{ syncing ? "Syncing..." : "Sync from PrintNode" }}
        </button>
      </div>

      <div v-if="loadingPrinters" class="text-gray-500 text-sm">
        Loading printers...
      </div>

      <div
        v-if="!loadingPrinters && printers.length === 0"
        class="text-gray-500 text-sm"
      >
        No printers found. Click "Sync from PrintNode" to import them.
      </div>

      <table v-if="printers.length > 0" class="w-full text-sm">
        <thead class="bg-gray-50">
          <tr>
            <th class="text-left p-2">Printer</th>
            <th class="text-left p-2">Computer</th>
            <th class="text-center p-2">State</th>
            <th class="text-center p-2">Paper</th>
            <th class="text-center p-2">Packing Slip</th>
            <th class="text-center p-2">Shipping Label</th>
            <th class="text-center p-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="printer in printers" :key="printer.id" class="border-t">
            <td class="p-2">
              <div>{{ printer.name }}</div>
              <div v-if="printer.description" class="text-xs text-gray-400">
                {{ printer.description }}
              </div>
            </td>
            <td class="p-2 text-gray-500">
              {{ printer.computerName || "-" }}
            </td>
            <td class="p-2 text-center">
              <span
                class="text-xs px-2 py-0.5 rounded"
                :class="printerStateClass(printer.state)"
              >
                {{ printer.state || "unknown" }}
              </span>
            </td>
            <td class="p-2 text-center">
              <select
                class="border rounded text-xs px-1 py-0.5"
                :value="printer.paperSize || ''"
                :disabled="printer._settingPaper"
                @change="setPaperSize(printer, $event.target.value)"
              >
                <option value="">Default</option>
                <option
                  v-for="opt in paperOptions(printer)"
                  :key="opt"
                  :value="opt"
                >
                  {{ opt }}
                </option>
              </select>
            </td>
            <td class="p-2 text-center">
              <button
                class="text-xs px-2 py-0.5 rounded"
                :class="
                  hasRole(printer, 'order_packing_slip')
                    ? 'bg-green-200 text-green-800'
                    : 'bg-gray-200 text-gray-600 hover:bg-blue-100'
                "
                @click="toggleRole(printer, 'order_packing_slip')"
                :disabled="printer._settingRole"
              >
                {{
                  hasRole(printer, "order_packing_slip") ? "★ Active" : "Set"
                }}
              </button>
            </td>
            <td class="p-2 text-center">
              <button
                class="text-xs px-2 py-0.5 rounded"
                :class="
                  hasRole(printer, 'shipping_label')
                    ? 'bg-green-200 text-green-800'
                    : 'bg-gray-200 text-gray-600 hover:bg-blue-100'
                "
                @click="toggleRole(printer, 'shipping_label')"
                :disabled="printer._settingRole"
              >
                {{ hasRole(printer, "shipping_label") ? "★ Active" : "Set" }}
              </button>
            </td>
            <td class="p-2 text-center">
              <button
                class="text-xs px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                @click="testPrint(printer)"
                :disabled="printer._testing"
              >
                {{ printer._testing ? "Printing..." : "Test Print" }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Recent Jobs Section -->
    <div class="mb-6 border rounded-lg p-4">
      <div class="flex items-center justify-between mb-3">
        <h2 class="text-lg font-semibold">Recent Print Jobs</h2>
        <button class="dark-button text-sm" @click="loadJobs">Refresh</button>
      </div>

      <div v-if="loadingJobs" class="text-gray-500 text-sm">
        Loading jobs...
      </div>

      <div
        v-if="!loadingJobs && jobs.length === 0"
        class="text-gray-500 text-sm"
      >
        No print jobs yet.
      </div>

      <table v-if="jobs.length > 0" class="w-full text-sm">
        <thead class="bg-gray-50">
          <tr>
            <th class="text-left p-2">Title</th>
            <th class="text-left p-2">Printer</th>
            <th class="text-left p-2">Source</th>
            <th class="text-center p-2">State</th>
            <th class="text-left p-2">URL Requested</th>
            <th class="text-left p-2">User</th>
            <th class="text-left p-2">Date</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="job in jobs" :key="job.id" class="border-t">
            <td class="p-2">{{ job.title }}</td>
            <td class="p-2 text-gray-500">{{ job.printerName || "-" }}</td>
            <td class="p-2 text-gray-500">{{ job.source || "-" }}</td>
            <td class="p-2 text-center">
              <span
                class="text-xs px-2 py-0.5 rounded"
                :class="jobStateClass(job.state)"
              >
                {{ job.state || "unknown" }}
              </span>
            </td>
            <td
              class="p-2 text-xs"
              style="max-width: 260px; word-break: break-all;"
            >
              <a
                v-if="job.contentUrl"
                :href="job.contentUrl"
                target="_blank"
                class="text-blue-600 hover:underline"
              >
                {{ job.contentUrl }}
              </a>
              <span v-else class="text-gray-400">-</span>
            </td>
            <td class="p-2 text-gray-500 text-xs">{{ job.createdBy }}</td>
            <td class="p-2 text-gray-500 text-xs">
              {{ formatDate(job.createdAt) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import PrintNodeApi from "@/api/PrintNode";

export default {
  name: "PrintNodePage",
  data() {
    return {
      // Settings
      settings: {
        apiKeySet: false,
        apiKeyMasked: "",
        enabled: "0",
        defaultPrinterId: null
      },
      apiKeyInput: "",
      savingKey: false,
      testingConnection: false,
      connectionResult: "",
      connectionOk: false,

      // Printers
      printers: [],
      loadingPrinters: false,
      syncing: false,

      // Jobs
      jobs: [],
      loadingJobs: false
    };
  },
  mounted() {
    this.loadSettings();
    this.loadPrinters();
    this.loadJobs();
  },
  methods: {
    // ── Settings ──
    async loadSettings() {
      try {
        const res = await PrintNodeApi.getSettings();
        const d = res.data || {};
        this.settings = {
          apiKeySet: d.apiKeySet || false,
          apiKeyMasked: d.apiKeyMasked || "",
          enabled: d.enabled || "0",
          defaultPrinterId: d.defaultPrinterId || null
        };
      } catch {
        this.toast("Error loading settings", "error");
      }
    },

    async saveApiKey() {
      this.savingKey = true;
      try {
        await PrintNodeApi.updateSetting("api_key", this.apiKeyInput);
        this.apiKeyInput = "";
        await this.loadSettings();
        this.toast("API key saved", "success");
      } catch {
        this.toast("Error saving API key", "error");
      } finally {
        this.savingKey = false;
      }
    },

    async toggleEnabled() {
      const newVal = this.settings.enabled === "1" ? "0" : "1";
      try {
        await PrintNodeApi.updateSetting("enabled", newVal);
        this.settings.enabled = newVal;
      } catch {
        this.toast("Error updating setting", "error");
      }
    },

    async testConnection() {
      this.testingConnection = true;
      this.connectionResult = "";
      try {
        const res = await PrintNodeApi.testConnection();
        const d = res.data || {};
        this.connectionOk = true;
        this.connectionResult = `Connected! Account: ${d.email ||
          d.firstname ||
          "OK"}`;
      } catch (err) {
        this.connectionOk = false;
        const msg =
          err.response && err.response.data && err.response.data.error
            ? err.response.data.error
            : "Connection failed";
        this.connectionResult = msg;
      } finally {
        this.testingConnection = false;
      }
    },

    // ── Printers ──
    async loadPrinters() {
      this.loadingPrinters = true;
      try {
        const res = await PrintNodeApi.getPrinters();
        this.printers = (res.data || []).map(p => ({
          ...p,
          _testing: false,
          _settingRole: false
        }));
      } catch {
        this.toast("Error loading printers", "error");
      } finally {
        this.loadingPrinters = false;
      }
    },

    async syncPrinters() {
      this.syncing = true;
      try {
        const res = await PrintNodeApi.sync();
        const d = res.data || {};
        this.toast(
          `Synced ${d.computersSynced || 0} computers and ${d.printersSynced ||
            0} printers`,
          "success"
        );
        await this.loadPrinters();
      } catch (err) {
        const msg =
          err.response && err.response.data && err.response.data.error
            ? err.response.data.error
            : "Error syncing from PrintNode";
        this.toast(msg, "error");
      } finally {
        this.syncing = false;
      }
    },

    paperOptions(printer) {
      const standard = ["A4", "Letter"];
      const dynamic = printer.paper
        ? String(printer.paper).split(",").map(p => p.trim()).filter(Boolean)
        : [];
      return [...new Set([...standard, ...dynamic])];
    },

    async setPaperSize(printer, paperSize) {
      this.$set(printer, "_settingPaper", true);
      try {
        await PrintNodeApi.setPaperSize(printer.id, paperSize);
        printer.paperSize = paperSize || null;
        this.toast(
          `${printer.name} paper set to ${paperSize || "Default"}`,
          "success"
        );
      } catch {
        this.toast("Error setting paper size", "error");
      } finally {
        this.$set(printer, "_settingPaper", false);
      }
    },

    hasRole(printer, role) {
      if (!printer || !printer.printerRole) return false;
      return String(printer.printerRole)
        .split(",")
        .map(r => r.trim())
        .includes(role);
    },

    async toggleRole(printer, role) {
      this.$set(printer, "_settingRole", true);
      const removing = this.hasRole(printer, role);
      try {
        if (removing) {
          await PrintNodeApi.removeRolePrinter(printer.id, role);
        } else {
          await PrintNodeApi.setRolePrinter(printer.id, role);
        }
        await this.loadPrinters();
        const labels = {
          order_packing_slip: "Packing Slip",
          shipping_label: "Shipping Label"
        };
        this.toast(
          `${printer.name} ${removing ? "unset" : "set"} as ${labels[role] ||
            role} printer`,
          "success"
        );
      } catch {
        this.toast("Error updating printer role", "error");
      } finally {
        this.$set(printer, "_settingRole", false);
      }
    },

    async testPrint(printer) {
      this.$set(printer, "_testing", true);
      try {
        await PrintNodeApi.testPrint(printer.printnodeId);
        this.toast(`Test print sent to ${printer.name}`, "success");
        await this.loadJobs();
      } catch (err) {
        const msg =
          err.response && err.response.data && err.response.data.error
            ? err.response.data.error
            : "Error sending test print";
        this.toast(msg, "error");
      } finally {
        this.$set(printer, "_testing", false);
      }
    },

    // ── Jobs ──
    async loadJobs() {
      this.loadingJobs = true;
      try {
        const res = await PrintNodeApi.getJobs();
        this.jobs = res.data || [];
      } catch {
        this.toast("Error loading jobs", "error");
      } finally {
        this.loadingJobs = false;
      }
    },

    // ── Helpers ──
    printerStateClass(state) {
      if (state === "online" || state === "idle")
        return "bg-green-200 text-green-800";
      if (state === "offline" || state === "error")
        return "bg-red-200 text-red-800";
      return "bg-gray-200 text-gray-600";
    },

    jobStateClass(state) {
      if (state === "submitted" || state === "done")
        return "bg-green-200 text-green-800";
      if (state === "error") return "bg-red-200 text-red-800";
      return "bg-gray-200 text-gray-600";
    },

    formatDate(dateStr) {
      return dateStr || "-";
    },

    toast(message, type) {
      this.$toast.open({
        message,
        type,
        position: "top-right"
      });
    }
  }
};
</script>
